import { supabase } from "@/integrations/supabase/client";

interface ReportPayload {
  scope?: string;
  message: string;
  stack?: string | null;
  componentStack?: string | null;
}

// In-memory dedupe: don't spam the backend with identical crashes on retry.
const recentlySent = new Map<string, number>();
const DEDUPE_WINDOW_MS = 30_000;

/**
 * Fire-and-forget crash reporter. Safe to call from componentDidCatch or
 * global error handlers - never throws, never blocks render.
 */
export async function reportClientError(payload: ReportPayload): Promise<void> {
  try {
    const key = `${payload.scope ?? ""}|${payload.message}`;
    const now = Date.now();
    const last = recentlySent.get(key);
    if (last && now - last < DEDUPE_WINDOW_MS) return;
    recentlySent.set(key, now);
    // Trim map opportunistically.
    if (recentlySent.size > 100) {
      for (const [k, t] of recentlySent) {
        if (now - t > DEDUPE_WINDOW_MS) recentlySent.delete(k);
      }
    }

    await supabase.functions.invoke("log-client-error", {
      body: {
        scope: payload.scope,
        message: payload.message,
        stack: payload.stack ?? undefined,
        componentStack: payload.componentStack ?? undefined,
        url: typeof window !== "undefined" ? window.location.href : undefined,
        userAgent:
          typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      },
    });
  } catch {
    // Never let telemetry crash the app.
  }
}
